import { Type } from '@angular/core';

import { CustomInputComponentInterface, ElementMapping } from '../client/integrations-client.types';

const MISC_ELEMENT_MAPPING = [
  [
    'do-greet',
    () =>
      import('../misc/greeter/greeter.component').then(
        c => <Type<CustomInputComponentInterface>>c.GreeterComponent,
      ),
  ],
  [
    'rti-new-integrations-badge',
    () =>
      import('../misc/new-integrations-badge/new-integrations-badge.component').then(
        c => c.NewIntegrationsBadgeComponent,
      ),
  ],
  [
    'rti-report-badges',
    () =>
      import('../misc/report-badges/report-badges.component').then(c => c.ReportBadgesComponent),
  ],
  [
    'rti-report-badge',
    () => import('../misc/report-badge/report-badge.component').then(c => c.ReportBadgeComponent),
  ],
  [
    'rti-partner-badge',
    () =>
      import('../misc/partner-badge/partner-badge.component').then(c => c.PartnerBadgeComponent),
  ],
  [
    'rti-error-messages-wrapper',
    () =>
      import('../misc/error-messages-wrapper/error-messages-wrapper.component').then(
        c => c.ErrorMessagesWrapperComponent,
      ),
  ],
] satisfies ElementMapping;

const DYNAMIC_ELEMENT_MAPPING = [
  [
    'rti-candidate-profile-badge-simple',
    () =>
      import('../dynamic/candidate-profile-badge-simple/candidate-profile-badge-simple.component').then(
        c => c.CandidateProfileBadgeSimpleComponent,
      ),
  ],
  [
    'rti-more-item-send-candidate',
    () =>
      import('../dynamic/more-item-send-candidate/more-item-send-candidate.component').then(
        c => c.MoreItemSendCandidateComponent,
      ),
  ],
] satisfies ElementMapping;

const ELECTRONIC_SIGNATURES_ELEMENT_MAPPING = [
  [
    'rti-electronic-signatures-more-item-send-signature-request',
    () =>
      import('./electronic-signatures/electronic-signatures-more-item-send-signature-request/electronic-signatures-more-item-send-signature-request.component').then(
        c => c.ElectronicSignaturesMoreItemSendSignatureRequestComponent,
      ),
  ],
  [
    'rti-electronic-signatures-candidate-reports',
    () =>
      import('./electronic-signatures/electronic-signatures-candidate-reports/electronic-signatures-candidate-reports.component').then(
        c => c.ElectronicSignaturesCandidateReportsComponent,
      ),
  ],
] satisfies ElementMapping;
const GFEA_ELEMENT_MAPPING = [
  [
    'rti-gfea-job-board',
    () =>
      import('./gfea/gfea-job-board/gfea-job-board.component').then(c => c.GfeaJobBoardComponent),
  ],
] satisfies ElementMapping;
const PREDICTIVE_INDEX_ELEMENT_MAPPING = [
  [
    'rti-predictive-index-candidate-reports',
    () =>
      import('./predictive-index/predictive-index-candidate-reports/predictive-index-candidate-reports.component').then(
        c => c.PredictiveIndexCandidateReportsComponent,
      ),
  ],
  [
    'rti-predictive-index-pi2-candidate-reports',
    () =>
      import('./predictive-index/predictive-index-pi2-candidate-reports/predictive-index-pi2-candidate-reports.component').then(
        c => c.PredictiveIndexPI2CandidateReportsComponent,
      ),
  ],
] satisfies ElementMapping;
const HOTELPROFESSIONALS_ELEMENT_MAPPING = [
  [
    'rti-hotelprofessionals-job-board',
    () =>
      import('./hotelprofessionals/hotelprofessionals-job-board/hotelprofessionals-job-board.component').then(
        c => c.HotelprofessionalsJobBoardComponent,
      ),
  ],
] satisfies ElementMapping;
const WERKEN_IN_EEN_HOTEL_ELEMENT_MAPPING = [
  [
    'rti-werken-in-een-hotel-job-board',
    () =>
      import('./werken-in-een-hotel/werken-in-een-hotel-job-board/werken-in-een-hotel-job-board.component').then(
        c => c.WerkenInEenHotelJobBoardComponent,
      ),
  ],
] satisfies ElementMapping;

const PARTNER_ELEMENT_MAPPING = [
  [
    'rti-partner-candidate-reports',
    () =>
      import('./partner/partner-candidate-reports/partner-candidate-reports.component').then(
        c => c.PartnerCandidateReportsComponent,
      ),
  ],
  [
    'rti-partner-embed',
    () =>
      import('./partner/partner-embed/partner-embed.component').then(c => c.PartnerEmbedComponent),
  ],
  [
    'rti-partner-embed-trigger',
    () =>
      // This mapping is required to use integrations service for fetch elements from backend.
      // However this component is not meant to be used with rti-outlet.
      // Instead of forcing a dummy value with valid typespec, different component is used.
      import('./partner/partner-embed/partner-embed.component').then(c => c.PartnerEmbedComponent),
  ],
] satisfies ElementMapping;

const CHECKR_ELEMENT_MAPPING = [
  [
    'rti-checkr-candidate-reports',
    () =>
      import('./checkr/checkr-candidate-reports/checkr-candidate-reports.component').then(
        c => c.CheckrCandidateReportsComponent,
      ),
  ],
  [
    'rti-checkr-candidate-profile-more-item',
    () =>
      import('./checkr/checkr-candidate-profile-more-item/checkr-candidate-profile-more-item.component').then(
        c => c.CheckrCandidateProfileMoreItemComponent,
      ),
  ],
] satisfies ElementMapping;

const CODILITY_ELEMENT_MAPPING = [
  [
    'rti-codility-email-template-select',
    () =>
      import('./codility/codility-email-template-select/codility-email-template-select.component').then(
        c => c.CodilityEmailTemplateSelectComponent,
      ),
  ],
  [
    'rti-codility-candidate-profile-code-live-more-item',
    () =>
      import('./codility/codility-candidate-profile-code-live-more-item/codility-candidate-profile-code-live-more-item.component').then(
        c => c.CodilityCandidateProfileCodeLiveMoreItemComponent,
      ),
  ],
  [
    'rti-codility-candidate-reports',
    () =>
      import('./codility/codility-candidate-reports/codility-candidate-reports.component').then(
        c => c.CodilityCandidateReportsComponent,
      ),
  ],
] satisfies ElementMapping;

const TYOMARKKINATORI_ELEMENT_MAPPING = [
  [
    'rti-tyomarkkinatori-job-board',
    () =>
      import('./tyomarkkinatori/tyomarkkinatori-job-board/tyomarkkinatori-job-board.component').then(
        c => c.TyomarkkinatoriJobBoardComponent,
      ),
  ],
] satisfies ElementMapping;

const ADP_ELEMENT_MAPPING = [
  [
    'rti-adp-more-item-send-candidate',
    () =>
      import('./adp/adp-more-item-send-candidate/adp-more-item-send-candidate.component').then(
        c => c.AdpMoreItemSendCandidateComponent,
      ),
  ],
  [
    'rti-adp-import-admins',
    () =>
      import('./adp/adp-import-admins/adp-import-admins.component').then(
        c => c.AdpImportAdminsComponent,
      ),
  ],
] satisfies ElementMapping;

const LINKEDIN_ELEMENT_MAPPING = [
  [
    'rti-linkedin-applicant-highlights',
    () =>
      import('./linkedin/linkedin-applicant-highlights/linkedin-applicant-highlights.component').then(
        c => c.LinkedinApplicantHighlightsComponent,
      ),
  ],
] satisfies ElementMapping;

export const ELEMENT_MAPPING = [
  ...MISC_ELEMENT_MAPPING,
  ...DYNAMIC_ELEMENT_MAPPING,
  ...ELECTRONIC_SIGNATURES_ELEMENT_MAPPING,
  ...GFEA_ELEMENT_MAPPING,
  ...PREDICTIVE_INDEX_ELEMENT_MAPPING,
  ...HOTELPROFESSIONALS_ELEMENT_MAPPING,
  ...WERKEN_IN_EEN_HOTEL_ELEMENT_MAPPING,
  ...PARTNER_ELEMENT_MAPPING,
  ...CHECKR_ELEMENT_MAPPING,
  ...CODILITY_ELEMENT_MAPPING,
  ...TYOMARKKINATORI_ELEMENT_MAPPING,
  ...ADP_ELEMENT_MAPPING,
  ...LINKEDIN_ELEMENT_MAPPING,
] satisfies ElementMapping;
